/**
 * AddUnexpectedItemPopover — BLU-516: "TL sometimes ships something we
 * didn't order." Lets a coordinator add a brand-new catalog SKU line to a
 * PO that's already submitted/in_transit/partially_received, right from the
 * Received tab, so it's immediately receivable — no going back to edit the
 * whole order.
 *
 * SKU source mirrors OrderCreate's picker (usePurchaseableSkus) — the full
 * org catalog for the PO's location, not just what's already stocked there.
 */
import { useMemo, useState } from "react";
import { Check, Loader2, PackagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePurchaseableSkus } from "@/features/inventory/hooks/usePurchaseableSkus";

interface AddUnexpectedItemPopoverProps {
  locationId: string | null | undefined;
  /** SKUs already on this order — excluded from the picker so a surprise
   *  arrival of an already-ordered SKU goes through "Receive Now" instead
   *  of creating a duplicate line. */
  existingSkuIds: Set<string>;
  onAdd: (skuId: string, quantity: number) => Promise<void>;
  disabled?: boolean;
}

export function AddUnexpectedItemPopover({
  locationId,
  existingSkuIds,
  onAdd,
  disabled,
}: AddUnexpectedItemPopoverProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<{ id: string; sku: string; title?: string } | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [submitting, setSubmitting] = useState(false);

  const { products } = usePurchaseableSkus(locationId);

  const options = useMemo(
    () => products.filter((product) => !existingSkuIds.has(product.id)),
    [products, existingSkuIds]
  );

  const reset = () => {
    setSelected(null);
    setQuantity("1");
  };

  const handleOpenChange = (next: boolean) => {
    if (submitting) return;
    setOpen(next);
    if (!next) reset();
  };

  const parsedQuantity = parseInt(quantity, 10);
  const quantityValid = !isNaN(parsedQuantity) && parsedQuantity > 0;

  const handleSubmit = async () => {
    if (!selected || !quantityValid) return;
    setSubmitting(true);
    try {
      await onAdd(selected.id, parsedQuantity);
      setOpen(false);
      reset();
    } catch {
      // Error toast handled by the mutation hook — keep the popover open
      // (with the selection intact) so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled}>
          <PackagePlus className="h-4 w-4 mr-2" />
          Add unexpected SKU
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        {!selected ? (
          <Command>
            <CommandInput placeholder="Search SKUs…" />
            <CommandList>
              <CommandEmpty>No SKUs found.</CommandEmpty>
              <CommandGroup>
                {options.map((product) => (
                  <CommandItem
                    key={product.id}
                    value={`${product.sku} ${product.title ?? ""}`}
                    onSelect={() =>
                      setSelected({ id: product.id, sku: product.sku, title: product.title })
                    }
                    className="gap-2"
                  >
                    <span className="font-mono text-xs font-medium shrink-0">{product.sku}</span>
                    {product.title && (
                      <span className="truncate text-xs text-muted-foreground">{product.title}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-xs font-medium truncate">{selected.sku}</p>
                {selected.title && (
                  <p className="truncate text-xs text-muted-foreground">{selected.title}</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs shrink-0"
                onClick={reset}
                disabled={submitting}
              >
                Change
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                inputMode="numeric"
                className="h-8 w-20 text-right tabular-nums"
                value={quantity}
                onChange={(e) => {
                  if (e.target.value === "" || /^\d+$/.test(e.target.value)) {
                    setQuantity(e.target.value);
                  }
                }}
                disabled={submitting}
                autoFocus
              />
              <span className="text-xs text-muted-foreground">units received</span>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSubmit}
                disabled={submitting || !quantityValid}
              >
                {submitting ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                )}
                Add
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
